import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  Row,
  Col,
  Typography,
  Button,
  Separator,
  Alert,
  Icon,
} from '@repo/ui/components'
import { useErrorHandler, useTranslate } from '@repo/shared/hooks'
import {
  InputCurrency,
  ValueWithUnit,
  PaymentType,
  FlatAsset,
} from '@repo/shared/components'
import * as React from 'react'
import { ButtonLink } from '@repo/shared/components'
import { useTransferUserAsset, useUserPortfolio } from '@repo/services'
import { useRouter } from '@repo/shared/i18n/navigation'
interface TransferDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  assetTitle: string
  assetToken: string
  amount: number
  fee: number
  feeCurrency: string
  transferTo: string
  amountInIRR: number
  selectedAsset: FlatAsset | null
}

export const TransferDialog: React.FC<TransferDialogProps> = (
  props: TransferDialogProps,
) => {
  const {
    open,
    onOpenChange,
    amount,
    amountInIRR,
    fee,
    feeCurrency,
    assetTitle,
    assetToken,
    transferTo,
    selectedAsset,
  } = props

  const { t } = useTranslate()

  const { push } = useRouter()
  const { data: { data: { accounts = [] } = {} } = {}, isLoading } =
    useUserPortfolio()
  const availableBalance =
    accounts?.find((account) => account.type === 'fiat')?.availableBalance ?? 0

  const isError = fee > availableBalance

  const { errorHandler } = useErrorHandler()
  const { mutate: transferUserAsset, isPending: isTransferUserAssetPending } =
    useTransferUserAsset({
      invalidate: true,
    })

  const handleConfirm = () => {
    transferUserAsset(
      {
        amount: Number((amount * (selectedAsset?.scale || 1)).toFixed(0)),
        destinationEmail: transferTo.includes('@') ? transferTo : undefined,
        destinationPhone: !transferTo.includes('@')
          ? transferTo.replace('09', '+989')
          : undefined,
        currency: assetToken,
        preview: false,
      },
      {
        onSuccess: () => {
          push(`/wallet/success`)
        },
        onError: errorHandler,
      },
    )
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className='md:max-w-[480px]'>
        <ModalHeader>
          <ModalTitle>
            <Col className='gap-2xs'>
              <Typography size='sm' className='text-center'>
                {t('transfer_dialog_title')}
              </Typography>
              <Typography size='md' weight='bold' className='text-center'>
                {assetTitle}
              </Typography>
            </Col>
          </ModalTitle>
        </ModalHeader>
        <Col className='gap-md py-md'>
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-80'>
              {t('property_id')}
            </Typography>
            <Typography size='md' className='font-[AzarMehr]'>
              {assetToken}
            </Typography>
          </Row>
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-80'>
              {t('transfer_amount')}
            </Typography>
            <ValueWithUnit
              value={amount}
              unit={
                feeCurrency === 'IRR' || feeCurrency === 'MALEK'
                  ? 'property'
                  : 'crypto'
              }
              scale={selectedAsset?.scale}
              unitSize='xs'
              valueSize='md'
              valueWeight='regular'
            />
          </Row>
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-80'>
              {t('transfer_estimated_value')}
            </Typography>
            <PaymentType
              value={amountInIRR}
              size='xs'
              numberSize='md'
              isBold={false}
            />
          </Row>
          <Row className='justify-between'>
            <Typography size='sm' color='text-neutral-surface-100'>
              {t('transfer_destination')}
            </Typography>
            <Typography size='md'>{transferTo}</Typography>
          </Row>
        </Col>
        <Separator />
        <Row className='justify-between my-sm'>
          <Typography size='sm' color='text-neutral-surface-80'>
            {t('transfer_transfer_fee')}
          </Typography>
          <PaymentType
            value={fee}
            size='xs'
            numberSize='md'
            inputCurrency={feeCurrency as InputCurrency}
          />
        </Row>

        {!isError && (
          <Alert
            color='alert'
            caption={t('transfer_dialog_hint')}
            icon={<Icon name='AlertTriangle' />}
            className='mb-xs'
          />
        )}
        {isError && (
          <Alert
            color='negative'
            caption={t('transfer_dialog_error')}
            hideIcon
            className='mb-xs'
          >
            <ButtonLink
              variant='fill'
              color='neutral'
              width='full'
              size='sm'
              href='/wallet/deposit'
              startIcon={<Icon name='Plus' size='sm' />}
            >
              {t('transfer_dialog_error_button')}
            </ButtonLink>
          </Alert>
        )}
        <ModalFooter>
          <Button
            variant='fill'
            color={'base'}
            width='full'
            size='lg'
            onClick={handleConfirm}
            disabled={isLoading || isError}
            loading={isLoading || isTransferUserAssetPending}
          >
            {t('transfer_dialog_confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
